import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const STORAGE_KEY_PREFIX = "advent_demo_seeded_";

/**
 * Seeds a demo world into a brand-new user's account on their first sign-in.
 *
 * How it works:
 *  1. Fires once per user per browser (guarded by localStorage).
 *  2. Reads `app_settings.demo_world_id` — if null/missing, silently no-ops.
 *     Operators set this to the UUID of the curated starter world in the
 *     Supabase dashboard or via the admin UI.
 *  3. Checks that the user actually has 0 worlds (prevents accidental re-clone
 *     if the localStorage flag is somehow cleared).
 *  4. Calls the clone-world edge function to produce a personal copy.
 *  5. Shows a welcome toast.
 *
 * Mount this hook once, outside all route components, so it persists for the
 * entire authenticated session (see App.tsx — same pattern as useDailyLoginBonus).
 */
export function useDemoWorldSeed() {
  const attemptedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (
          (event === "SIGNED_IN" || event === "INITIAL_SESSION") &&
          session?.user
        ) {
          const userId = session.user.id;

          // Skip if we've already attempted this user in this page lifetime
          if (attemptedRef.current.has(userId)) return;
          attemptedRef.current.add(userId);

          // Skip if already seeded for this user (persists across reloads)
          const storageKey = STORAGE_KEY_PREFIX + userId;
          if (localStorage.getItem(storageKey)) return;

          // Non-blocking — any failure is silently swallowed
          seedDemoWorld(userId, session.access_token, storageKey).catch(() => {});
        }

        if (event === "SIGNED_OUT") {
          attemptedRef.current.clear();
        }
      },
    );

    return () => subscription.unsubscribe();
  }, []);
}

async function seedDemoWorld(
  userId: string,
  accessToken: string,
  storageKey: string,
): Promise<void> {
  // 1. Read the configured demo world ID from app_settings
  const { data: setting } = await supabase
    .from("app_settings")
    .select("value")
    .eq("key", "demo_world_id")
    .maybeSingle();

  const demoWorldId =
    setting?.value && typeof setting.value === "string" && setting.value !== "null"
      ? setting.value
      : null;

  // No demo world configured — operator hasn't set one yet
  if (!demoWorldId) return;

  // 2. Check world count — only seed for users with 0 worlds
  const { count } = await supabase
    .from("worlds")
    .select("id", { count: "exact", head: true })
    .eq("user_id", userId);

  if ((count ?? 0) > 0) {
    // User already has worlds — mark as seeded so we don't check again
    localStorage.setItem(storageKey, "1");
    return;
  }

  // 3. Clone the demo world into the user's account
  const { functionUrl, SUPABASE_PUBLISHABLE_KEY } = await import("@/lib/supabase-env");
  const anonKey = SUPABASE_PUBLISHABLE_KEY;

  const resp = await fetch(functionUrl("clone-world"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
      apikey: anonKey,
    },
    body: JSON.stringify({
      source_world_id: demoWorldId,
      new_name: "My First World (Demo)",
    }),
  });

  if (!resp.ok) {
    // Log but don't surface the error to the user — demo seeding is non-critical
    const err = await resp.json().catch(() => ({}));
    console.warn("[useDemoWorldSeed] clone-world failed:", err);
    return;
  }

  // 4. Mark as seeded so we never attempt again
  localStorage.setItem(storageKey, "1");

  // 5. Welcome toast
  toast.success("Your first world is ready!", {
    description: 'Check out "My First World (Demo)" to explore the platform.',
    duration: 6000,
  });
}
